import { useMemo, useState } from "react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { ChevronLeft, ChevronRight, RotateCcw } from "lucide-react";
import { AVAILABILITY } from "../lib/testIds";

// ---- Date helpers (timezone-stable, work with YYYY-MM-DD only) ----
const toIso = (d) => {
    if (!d) return null;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
};
const fromIso = (s) => {
    if (!s) return null;
    const [y, m, d] = s.split("-").map(Number);
    return new Date(y, m - 1, d);
};
const sameDay = (a, b) =>
    !!a &&
    !!b &&
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
const eachDay = (start, end) => {
    if (!start || !end) return [];
    const out = [];
    const cur = new Date(start);
    while (cur <= end) {
        out.push(toIso(cur));
        cur.setDate(cur.getDate() + 1);
    }
    return out;
};

const NavButton = ({ direction, ...rest }) => (
    <button
        type="button"
        {...rest}
        aria-label={direction === "prev" ? "Previous month" : "Next month"}
        className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#2e3220]/5 text-[#2e3220] transition-colors hover:bg-[#737a3d]/15 disabled:opacity-30"
    >
        {direction === "prev" ? (
            <ChevronLeft className="h-4 w-4" strokeWidth={2.5} />
        ) : (
            <ChevronRight className="h-4 w-4" strokeWidth={2.5} />
        )}
    </button>
);

const LEGEND = [
    { label: "Available", className: "bg-white border border-[#2e3220]/15" },
    {
        label: "Booked",
        className: "bg-[#B56C5A]/15 border border-[#B56C5A]/40 line-through",
    },
    {
        label: "Unavailable",
        className: "bg-[#2e3220]/10 border border-[#2e3220]/30",
    },
    {
        label: "Selected",
        className: "bg-[#737a3d] text-[#F4F1EA]",
    },
];

const Legend = ({ mode }) => {
    const items =
        mode === "edit"
            ? LEGEND.filter(
                  (l) => l.label !== "Selected" && l.label !== "Booked"
              ).concat({
                  label: "Booked",
                  className: "bg-[#B56C5A]/15 border border-[#B56C5A]/40 line-through",
              })
            : mode === "view"
              ? LEGEND.filter((l) => l.label !== "Selected")
              : LEGEND;
    return (
        <ul
            data-testid={AVAILABILITY.legend}
            className="flex flex-wrap gap-x-4 gap-y-2 text-xs text-[#5a6142]"
        >
            {items.map((l) => (
                <li key={l.label} className="inline-flex items-center gap-2">
                    <span
                        className={`inline-block h-4 w-4 rounded-md ${l.className}`}
                    />
                    {l.label}
                </li>
            ))}
        </ul>
    );
};

/**
 * mode = "view"   read-only, owner-blocked + booked dates dimmed/struck through
 * mode = "select" date range picker for renter checkout (disables unavailable)
 * mode = "edit"   owner toggles blocked dates (click any future date to add/remove block)
 */
export const AvailabilityCalendar = ({
    mode = "view",
    availability,             // { blocked_dates, booked_dates, unavailable_dates }
    minDate,                  // Date — defaults to today
    range,                    // { from: Date, to: Date } for select mode
    onRangeChange,            // (range) => void
    blockedDates = [],        // ISO strings, used in edit mode
    onToggleDate,             // (iso) => void, used in edit mode
    className = "",
}) => {
    const today = minDate || new Date();
    today.setHours(0, 0, 0, 0);

    const bookedSet = useMemo(
        () => new Set(availability?.booked_dates || []),
        [availability],
    );
    const ownerBlockedSet = useMemo(() => {
        if (mode === "edit") return new Set(blockedDates);
        return new Set(availability?.blocked_dates || []);
    }, [availability, blockedDates, mode]);
    const unavailableSet = useMemo(
        () => new Set([...bookedSet, ...ownerBlockedSet]),
        [bookedSet, ownerBlockedSet],
    );

    const bookedMatcher = (d) => bookedSet.has(toIso(d));
    const ownerBlockedMatcher = (d) => ownerBlockedSet.has(toIso(d));
    const pastMatcher = (d) => d < today;

    const modifiers = {
        booked: bookedMatcher,
        ownerBlocked: ownerBlockedMatcher,
    };
    const modifiersClassNames = {
        booked:
            "!bg-[#B56C5A]/15 !text-[#7a3a30] !line-through hover:!bg-[#B56C5A]/15 cursor-not-allowed",
        ownerBlocked:
            "!bg-[#2e3220]/10 !text-[#2e3220]/60 hover:!bg-[#2e3220]/10",
    };

    const classNames = {
        months: "flex flex-col gap-4",
        month: "space-y-2",
        caption: "flex justify-between items-center px-1 py-1",
        caption_label:
            "font-display text-base font-bold text-[#2e3220] tracking-tight",
        nav: "flex items-center gap-1",
        nav_button:
            "inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#2e3220]/5 text-[#2e3220] transition-colors hover:bg-[#737a3d]/15",
        nav_button_previous: "",
        nav_button_next: "",
        table: "w-full border-collapse",
        head_row: "flex",
        head_cell:
            "w-9 sm:w-10 text-[10px] uppercase tracking-widest font-bold text-[#737a3d] text-center pb-2",
        row: "flex w-full",
        cell: "relative p-0 text-center",
        day: "inline-flex h-9 w-9 sm:h-10 sm:w-10 items-center justify-center rounded-full text-sm text-[#2e3220] hover:bg-[#737a3d]/15 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#737a3d]",
        day_today: "ring-1 ring-[#737a3d]/40 font-bold",
        day_selected:
            "!bg-[#737a3d] !text-[#F4F1EA] hover:!bg-[#5f6534] focus:!bg-[#5f6534]",
        day_disabled: "text-[#2e3220]/25 cursor-not-allowed hover:bg-transparent",
        day_outside: "text-[#2e3220]/30",
        day_range_start: "!rounded-l-full",
        day_range_end: "!rounded-r-full",
        day_range_middle:
            "!bg-[#737a3d]/25 !text-[#2e3220] !rounded-none hover:!bg-[#737a3d]/30",
        day_hidden: "invisible",
    };

    const sharedProps = {
        showOutsideDays: false,
        fromDate: today,
        toDate: (() => {
            const d = new Date(today);
            d.setDate(d.getDate() + 120);
            return d;
        })(),
        modifiers,
        modifiersClassNames,
        classNames,
        components: {
            IconLeft: () => <ChevronLeft className="h-4 w-4" strokeWidth={2.5} />,
            IconRight: () => <ChevronRight className="h-4 w-4" strokeWidth={2.5} />,
        },
    };

    let dayPicker;
    if (mode === "view") {
        dayPicker = (
            <DayPicker
                mode="single"
                selected={undefined}
                disabled={[{ before: today }, ...Array.from(unavailableSet).map(fromIso)]}
                {...sharedProps}
            />
        );
    } else if (mode === "select") {
        const handleSelect = (r) => {
            if (!r) {
                onRangeChange?.({ from: null, to: null });
                return;
            }
            const expanded = eachDay(r.from, r.to || r.from);
            const conflict = expanded.some((iso) => unavailableSet.has(iso));
            if (conflict) {
                // Reject the new range — keep the existing one
                return;
            }
            onRangeChange?.(r);
        };
        dayPicker = (
            <DayPicker
                mode="range"
                selected={range}
                onSelect={handleSelect}
                disabled={(d) => pastMatcher(d) || unavailableSet.has(toIso(d))}
                {...sharedProps}
            />
        );
    } else {
        // edit
        const handleSelect = (dates) => {
            // For 'multiple' mode, react-day-picker returns the new full list
            // including newly-clicked OR removed. We expect parent to manage
            // state through onToggleDate but we need the diff:
            const newSet = new Set((dates || []).map(toIso));
            const oldSet = new Set(blockedDates);
            // Find dates to add
            for (const iso of newSet) {
                if (!oldSet.has(iso)) onToggleDate?.(iso, true);
            }
            for (const iso of oldSet) {
                if (!newSet.has(iso)) onToggleDate?.(iso, false);
            }
        };
        dayPicker = (
            <DayPicker
                mode="multiple"
                selected={blockedDates.map(fromIso)}
                onSelect={handleSelect}
                disabled={(d) => pastMatcher(d) || bookedSet.has(toIso(d))}
                modifiers={{
                    ...modifiers,
                    // In edit mode, ownerBlocked = selected, so don't double-style
                    ownerBlocked: () => false,
                }}
                {...sharedProps}
            />
        );
    }

    return (
        <div
            data-testid={AVAILABILITY.calendar(mode)}
            className={`rounded-3xl border border-[#2e3220]/10 bg-[#F4F1EA] p-4 sm:p-5 ${className}`}
        >
            {dayPicker}
            <div className="mt-4 border-t border-[#2e3220]/10 pt-3">
                <Legend mode={mode} />
            </div>
        </div>
    );
};

// Convenience: range-picker controller for the checkout flow
export const useAvailabilityRange = () => {
    const [range, setRange] = useState({ from: null, to: null });
    const days = useMemo(() => {
        if (!range.from) return 0;
        const end = range.to || range.from;
        return eachDay(range.from, end).length;
    }, [range]);
    return { range, setRange, days };
};

export { toIso, fromIso, eachDay, sameDay };
export default AvailabilityCalendar;
